import React, {
  useEffect,
  useContext,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link,
  useRouteMatch,
  useParams,
  useHistory,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Chart from "chart.js";
import moment from "moment";

import lifeAreasData from "../../resources/lifeAreas";

import style from "./Area.module.css";
import Container from "../../hoc/container/Container";
import ColourContext from "../../contexts/ColourContext";
import { colourThemes } from "../../styles/colourPalettes";
import LifeAreasContext from "../../contexts/LifeAreasContext";
import { setDataInLocalStorage } from "../../utils/storage";
import System from "./system/System";

import settingsIcon from "../../assets/icons/settings.svg";
import blocksData from "../../resources/blocks";
import ActivityContext from "../../contexts/ActivityContext";
import {
  returnProgressDataForCurrentWeek,
  isBlockCompletedToday,
} from "../../utils/progress";
import WindowContext from "../../contexts/WindowContext";

type AreaProps = {
  // key: string;
};

const Area = () => {
  // Colour Theme
  const { key: colourKey } = useContext(ColourContext);
  const colour = colourThemes[colourKey];
  // Window
  const { width } = useContext(WindowContext);
  // Area
  const history = useHistory();
  // const params = useParams();
  const { areaKey } = useParams();
  const lifeArea = lifeAreasData[areaKey] || { title: "" };

  // User life areas
  const { lifeAreas: areasState, dispatchLifeAreas } = useContext(
    LifeAreasContext
  );
  const [visionText, setVisionText] = useState(
    areasState.visions[areaKey] || ""
  );
  const nextLevelScore = Math.ceil(areasState.scores[areaKey]);
  // System
  const [showSystemEmptyState, setShowSystemEmptyState] = useState(false);
  // Activity
  const { activity, dispatchActivity } = useContext(ActivityContext);
  // Progress Chart
  const chartCanvas = useRef<HTMLCanvasElement>(null);
  const chartContainer = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(0);
  const [chartData, setChartData] = useState<number[]>([]);
  const chart = useRef<Chart>(); //ChartJs Chart

  // Redirect if an incorrect life area url has been entered
  // useEffect(() => {
  //   if (!lifeAreasData[areaKey]) return history.push("/personal");
  // }, [areaKey, history]);

  // TODO: On mount the areasState hasn't loaded
  useEffect(() => {
    const showEmptyState =
      areasState &&
      areasState.blockIds[areaKey] &&
      areasState.blockIds[areaKey].length === 0;
    setShowSystemEmptyState(showEmptyState);
  }, [areasState, areaKey]);

  // Create the bar chart
  useEffect(() => {
    // Show current 7 days of progress.
    const dataset: Array<number> = returnProgressDataForCurrentWeek(
      activity,
      areasState,
      areaKey
    );
    const maxCount = areasState.blockIds[areaKey].length;
    // https://www.chartjs.org/docs/latest/charts/bar.html
    const data = {
      labels: ["S", "M", "T", "W", "T", "F", "S"],
      datasets: [
        {
          data: dataset,
        },
      ],
    };
    const options = {
      legend: { display: false },
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              min: 0,
              stepSize: 1,
              max: maxCount,
            },
          },
        ],
      },
    };

    if (chartCanvas.current) {
      const ctx = chartCanvas.current;
      chart.current = new Chart(ctx, {
        type: "bar",
        data: data,
        options: options,
      });
    }

    if (chartContainer.current) {
      const chartHeight = chartContainer.current.getBoundingClientRect().height;
      console.log("chart data:", dataset);
      setChartHeight(chartHeight);
      setChartData(dataset);
    }
  }, [activity, areaKey, areasState]);

  // Vision
  const handleVisionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setVisionText(value);
  };

  const onVisionTextBlur = useCallback(() => {
    console.log("updating vision", visionText);
    dispatchLifeAreas({
      type: "SET_VISION",
      payload: {
        key: areaKey,
        text: visionText,
      },
    });
  }, [dispatchLifeAreas, areaKey, visionText]);

  // System Blocks
  const disableSystem = visionText.length === 0;

  const handleEditSystemPressed = useCallback(() => {
    const url = `${areaKey}/system`;
    history.push(url);
  }, [areaKey, history]);

  const handleBlockTapped = (blockId: string, isCompleted: boolean) => {
    if (!isCompleted) {
      dispatchActivity({
        type: "COMPLETE_BLOCK_FOR_TODAY",
        blockId,
      });
    } else {
      dispatchActivity({
        type: "UNCOMPLETE_BLOCK_FOR_TODAY",
        blockId,
      });
    }
  };

  // If a block has been completed in the last week don't show hints
  const showCompletionHints = useMemo(
    () => !chartData.filter((number) => number > 0).length,
    [chartData]
  );

  return (
    <AnimatePresence>
      <Switch>
        <Route path="/personal/:areaKey/system" exact={true}>
          <System />
        </Route>
      </Switch>

      <motion.section
        key="area"
        className={style.container}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Vision */}
        <Container containerClass={style.visionContainer}>
          <div className={style.score} style={{ color: colour }}>
            <h4>NEXT LEVEL</h4>
            <span>{nextLevelScore}</span>
          </div>
          <textarea
            name="visionText"
            value={visionText}
            onChange={handleVisionChange}
            onBlur={onVisionTextBlur}
            placeholder={`Describe your next level in ${lifeArea.title}. What will it look like? How will it feel?`}
          />
        </Container>

        {/* Success System */}
        <Container
          containerClass={style.systemContainer}
          containerStyle={{ color: colour }}
        >
          <h4>
            SYSTEM
            {!showSystemEmptyState &&
              showCompletionHints &&
              " (Tap to complete for the day)"}
          </h4>
          {/* Success system only enabled when a vision is entered */}
          <div className={style.systemBlocks}>
            {areasState.blockIds[areaKey].map((blockId: string) => {
              const isCompleted = isBlockCompletedToday(blockId, activity);
              return (
                <button
                  key={blockId}
                  onClick={() => handleBlockTapped(blockId, isCompleted)}
                  style={{ backgroundColor: isCompleted ? colour : "" }}
                >
                  {blocksData[blockId].title}
                </button>
              );
            })}
          </div>

          {/* Empty State */}
          {showSystemEmptyState && (
            <div className={style.systemEmptyState}>
              <p>
                “Success is a few simple disciplines practiced day in, day out.”
              </p>
              <button
                disabled={disableSystem}
                onClick={handleEditSystemPressed}
              >
                <img src={settingsIcon} alt="Settings" />
                Set Up
              </button>
            </div>
          )}
        </Container>
        {/* Progress */}
        <div className={style.chartContainer} ref={chartContainer}>
          <canvas
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: width,
              height: chartHeight,
            }}
            ref={chartCanvas}
          ></canvas>
        </div>
      </motion.section>
    </AnimatePresence>
  );
};

export default Area;
